/**
 * Mapping functions to transform API responses to UI data models
 */

import {
  EmployeeOptionsStatement,
  EmployeeBasicInfo,
  OptionAgreement,
  OptionTransaction,
  EmployeeOptionsSummary,
  ActiveOptionsData,
  CashlessExerciseData,
  PurchaseExerciseData,
  ActiveOptionsSummary,
  CashlessExerciseSummary,
  PurchaseExerciseSummary,
  TransactionType
} from '../types/employee-statement';
import {
  EmployeeApiResponse,
  OptionAgreementApiResponse,
  OptionTransactionApiResponse
} from '../types/api/employee-options-api';

export function mapEmployeeBasicInfo(apiEmployee: EmployeeApiResponse): EmployeeBasicInfo {
  return {
    employeeId: apiEmployee.employeeId,
    employeeNumber: apiEmployee.employeeNumber,
    firstName: apiEmployee.firstName,
    lastName: apiEmployee.lastName,
    fullName: `${apiEmployee.firstName} ${apiEmployee.lastName}`
  };
}

export function mapOptionTransaction(
  apiTransaction: OptionTransactionApiResponse,
  strikePrice: number
): OptionTransaction {
  const type = mapTransactionType(apiTransaction.optionTransactionTypeId);

  // Backend calculates vestedOptions as delta (positive for vesting, negative for exercises)
  const vestedOptions = apiTransaction.vestedOptions;

  // For exercises, use the delta fields to get the actual shares exercised
  const sharesExercisedThisTransaction = (type === TransactionType.CASHLESS_EXERCISE)
    ? apiTransaction.deltaCashlessExercised
    : (type === TransactionType.PURCHASE_EXERCISE)
    ? apiTransaction.deltaPurchaseExercised
    : 0;

  let proceeds = 0;
  let cost = 0;

  if (type === TransactionType.CASHLESS_EXERCISE) {
    proceeds = sharesExercisedThisTransaction * Math.max(0, apiTransaction.priceAtExercise - strikePrice);
  } else if (type === TransactionType.PURCHASE_EXERCISE) {
    cost = sharesExercisedThisTransaction * strikePrice;
  }

  // Format type name based on transaction type
  let typeName = apiTransaction.optionTransactionTypeName;

  if (type === TransactionType.GRANT) {
    typeName = 'Initial Grant (Unvested)';
  } else if (type === TransactionType.VEST && apiTransaction.vestPercentage && apiTransaction.vestAnniversary) {
    const years = apiTransaction.vestAnniversary;
    const percentage = apiTransaction.vestPercentage * 100;
    typeName = `${years} Year${years !== 1 ? 's' : ''}, ${percentage}% Vested`;
  }

  return {
    id: apiTransaction.optionAgreementTranId,
    optionAgreementId: apiTransaction.optionAgreementId,
    type,
    typeName,
    effectiveDate: apiTransaction.effectiveDate,
    vestedOptions,
    unvestedOptions: apiTransaction.unvestedOptions,
    sharesExercised: sharesExercisedThisTransaction,
    deltaVestedOptions: apiTransaction.deltaVestedOptions,
    deltaUnvestedOptions: apiTransaction.deltaUnvestedOptions,
    deltaCashlessExercised: apiTransaction.deltaCashlessExercised,
    deltaPurchaseExercised: apiTransaction.deltaPurchaseExercised,
    purchaseExercisedShares: apiTransaction.purchaseExercisedShares,
    priceAtExercise: apiTransaction.priceAtExercise,
    notes: apiTransaction.notes,
    proceeds,
    cost,
    netValue: proceeds - cost,
    vestPercentage: apiTransaction.vestPercentage,
    vestAnniversary: apiTransaction.vestAnniversary,
    vestedBalance: apiTransaction.vestedBalance,
    unvestedBalance: apiTransaction.unvestedBalance
  };
}

export function mapOptionAgreement(apiOption: OptionAgreementApiResponse): OptionAgreement {
  const transactions = (apiOption.transactions || [])
    .map(t => mapOptionTransaction(t, apiOption.strikePrice));

  const exerciseTransactions = transactions.filter(t =>
    t.type === TransactionType.CASHLESS_EXERCISE ||
    t.type === TransactionType.PURCHASE_EXERCISE
  );

  const cashlessExercisedShares = exerciseTransactions
    .filter(t => t.type === TransactionType.CASHLESS_EXERCISE)
    .reduce((sum, t) => sum + t.sharesExercised, 0);

  const purchaseExercisedShares = exerciseTransactions
    .filter(t => t.type === TransactionType.PURCHASE_EXERCISE)
    .reduce((sum, t) => sum + t.sharesExercised, 0);

  const cashlessProceeds = exerciseTransactions
    .filter(t => t.type === TransactionType.CASHLESS_EXERCISE)
    .reduce((sum, t) => sum + t.proceeds, 0);

  const purchaseCosts = exerciseTransactions
    .filter(t => t.type === TransactionType.PURCHASE_EXERCISE)
    .reduce((sum, t) => sum + t.cost, 0);

  const unexercisedShares = apiOption.totalShares - apiOption.exercisedShares;
  const vestedRemaining = apiOption.vestedShares - apiOption.exercisedShares;
  const priceDifference = apiOption.currentPrice - apiOption.strikePrice;
  const isInTheMoney = apiOption.currentPrice > apiOption.strikePrice;
  const vestedValue = vestedRemaining * Math.max(0, priceDifference);

  // Create filtered transaction arrays for different table contexts
  const cashlessExerciseTransactions = transactions
    .filter(t => t.type === TransactionType.CASHLESS_EXERCISE)
    .map(t => ({
      id: t.id,
      optionAgreementId: t.optionAgreementId,
      type: t.type,
      typeName: t.typeName,
      effectiveDate: t.effectiveDate,
      notes: t.notes,
      sharesExercised: t.sharesExercised,
      purchaseExercisedShares: t.purchaseExercisedShares,
      strikePrice: apiOption.strikePrice,
      priceAtExercise: t.priceAtExercise,
      priceDifference: t.priceAtExercise - apiOption.strikePrice,
      proceeds: t.proceeds,
      netValue: t.proceeds
    }));

  const purchaseExerciseTransactions = transactions
    .filter(t => t.type === TransactionType.PURCHASE_EXERCISE)
    .map(t => ({
      id: t.id,
      optionAgreementId: t.optionAgreementId,
      type: t.type,
      typeName: t.typeName,
      effectiveDate: t.effectiveDate,
      notes: t.notes,
      sharesPurchased: t.sharesExercised,
      strikePrice: apiOption.strikePrice,
      purchaseCost: t.cost
    }));

  const activeTransactions = transactions
    .filter(t => t.type === TransactionType.GRANT || t.type === TransactionType.VEST)
    .map(t => ({
      id: t.id,
      optionAgreementId: t.optionAgreementId,
      type: t.type,
      typeName: t.typeName,
      effectiveDate: t.effectiveDate,
      notes: t.notes
    }));

  return {
    optionAgreementId: apiOption.optionAgreementId,
    issueDate: apiOption.issueDate,
    expirationDate: apiOption.expirationDate,
    description: apiOption.description,
    totalShares: apiOption.totalShares,
    vestedShares: apiOption.vestedShares,
    unvestedShares: apiOption.unvestedShares || 0,
    exercisedShares: apiOption.exercisedShares,
    unexercisedShares,
    vestedRemaining,
    strikePrice: apiOption.strikePrice,
    currentPrice: apiOption.currentPrice,
    vestedPercentage: apiOption.vestedPercentage,
    vestedValue,
    priceDifference,
    isInTheMoney,
    cashlessExercisedShares,
    purchaseExercisedShares,
    cashlessProceeds,
    purchaseCosts,
    netExerciseValue: cashlessProceeds - purchaseCosts,
    nextVestDate: apiOption.nextVestDate || null,
    nextVestAmount: apiOption.nextVestAmount || null,
    transactions,
    cashlessExerciseTransactions,
    purchaseExerciseTransactions,
    activeTransactions,
    hasVestedOptions: vestedRemaining > 0,
    hasUnvestedOptions: (apiOption.unvestedShares || 0) > 0,
    hasExercisedOptions: apiOption.exercisedShares > 0,
    hasPurchaseTransactions: purchaseExercisedShares > 0
  };
}

function calculateActiveOptionsSummary(options: OptionAgreement[]): ActiveOptionsSummary {
  return {
    totalActiveOptions: options.reduce((sum, opt) => sum + opt.totalShares, 0),
    totalVestedOptions: options.reduce((sum, opt) => sum + opt.vestedRemaining, 0),
    totalVestedValue: options.reduce((sum, opt) => sum + opt.vestedValue, 0),
    currentPrice: options.length > 0 ? options[0].currentPrice : 0
  };
}

function calculateCashlessExerciseSummary(options: OptionAgreement[]): CashlessExerciseSummary {
  const totalInitialShares = options.reduce((sum, opt) => sum + opt.totalShares, 0);
  const totalCashlessExercisedOptions = options.reduce((sum, opt) => sum + opt.cashlessExercisedShares, 0);
  const totalPurchaseExercisedOptions = options.reduce((sum, opt) => sum + opt.purchaseExercisedShares, 0);
  const totalCashlessProceeds = options.reduce((sum, opt) => sum + opt.cashlessProceeds, 0);
  const totalPurchaseCosts = options.reduce((sum, opt) => sum + opt.purchaseCosts, 0);

  return {
    totalInitialShares,
    totalCashlessExercisedOptions,
    totalPurchaseExercisedOptions,
    totalAllExercisedOptions: totalCashlessExercisedOptions + totalPurchaseExercisedOptions,
    totalCashlessProceeds,
    totalPurchaseCosts,
    totalNetValue: totalCashlessProceeds - totalPurchaseCosts
  };
}

function calculatePurchaseExerciseSummary(options: OptionAgreement[]): PurchaseExerciseSummary {
  const totalInitialShares = options.reduce((sum, opt) => sum + opt.totalShares, 0);
  const totalPurchasedShares = options.reduce((sum, opt) => sum + opt.purchaseExercisedShares, 0);
  const totalCost = options.reduce((sum, opt) => sum + opt.purchaseCosts, 0);

  return {
    totalInitialShares,
    totalPurchasedOptions: totalPurchasedShares,
    totalPurchaseCost: totalCost,
    averageStrikePrice: totalPurchasedShares > 0 ? totalCost / totalPurchasedShares : 0
  };
}

export function mapEmployeeOptionsStatement(
  apiResponse: any
): EmployeeOptionsStatement {
  // The API response has employee data directly on the root object
  const employee = mapEmployeeBasicInfo({
    employeeId: apiResponse.employeeId,
    employeeNumber: apiResponse.employeeNumber,
    firstName: apiResponse.firstName,
    lastName: apiResponse.lastName
  });

  // Combine active options and exercised options, deduplicating by optionAgreementId
  // Some options may appear in both arrays (partially exercised)
  const optionsMap = new Map<number, OptionAgreement>();

  // Add active options
  (apiResponse.options || []).forEach((apiOption: OptionAgreementApiResponse) => {
    optionsMap.set(apiOption.optionAgreementId, mapOptionAgreement(apiOption));
  });

  // Add exercised options (merge with existing if already present)
  (apiResponse.exercisedOptions || []).forEach((apiOption: OptionAgreementApiResponse) => {
    if (!optionsMap.has(apiOption.optionAgreementId)) {
      optionsMap.set(apiOption.optionAgreementId, mapOptionAgreement(apiOption));
    }
  });

  const options = Array.from(optionsMap.values());

  // Categorize options for different tabs
  const vestedOptionsArray = options.filter(opt => opt.hasVestedOptions);
  const unvestedOptionsArray = options.filter(opt => opt.hasUnvestedOptions);
  const exercisedOptionsArray = options.filter(opt => opt.hasExercisedOptions);
  const purchaseOptionsArray = options.filter(opt => opt.hasPurchaseTransactions);

  // Create data structures with summaries
  const activeOptions: ActiveOptionsData = {
    options: vestedOptionsArray,
    summary: calculateActiveOptionsSummary(vestedOptionsArray),
    hasData: vestedOptionsArray.length > 0
  };

  const unvestedOptions: ActiveOptionsData = {
    options: unvestedOptionsArray,
    summary: calculateActiveOptionsSummary(unvestedOptionsArray),
    hasData: unvestedOptionsArray.length > 0
  };

  const cashlessExerciseOptions: CashlessExerciseData = {
    options: exercisedOptionsArray,
    summary: calculateCashlessExerciseSummary(exercisedOptionsArray),
    hasData: exercisedOptionsArray.length > 0
  };

  const purchaseExerciseOptions: PurchaseExerciseData = {
    options: purchaseOptionsArray,
    summary: calculatePurchaseExerciseSummary(purchaseOptionsArray),
    hasData: purchaseOptionsArray.length > 0
  };

  // Calculate overall summary
  const totalOptions = options.reduce((sum, opt) => sum + opt.totalShares, 0);
  const vestedOptions = options.reduce((sum, opt) => sum + opt.vestedShares, 0);
  const exercisedOptionsCount = options.reduce((sum, opt) => sum + opt.exercisedShares, 0);
  const vestedWithPositiveValue = options.reduce((sum, opt) => sum + (opt.vestedValue > 0 ? opt.vestedRemaining : 0), 0);
  const currentPrice = options.length > 0 ? options[0].currentPrice : 0;
  const totalCashlessProceeds = options.reduce((sum, opt) => sum + opt.cashlessProceeds, 0);
  const totalPurchaseCosts = options.reduce((sum, opt) => sum + opt.purchaseCosts, 0);
  const totalNetExerciseValue = totalCashlessProceeds - totalPurchaseCosts;

  const summary: EmployeeOptionsSummary = {
    totalOptions,
    vestedOptions,
    exercisedOptions: exercisedOptionsCount,
    vestedWithPositiveValue,
    currentPrice,
    totalCashlessProceeds,
    totalPurchaseCosts,
    totalNetExerciseValue
  };

  return {
    employee,
    summary,
    activeOptions,
    unvestedOptions,
    cashlessExerciseOptions,
    purchaseExerciseOptions,
    hasAnyOptions: options.length > 0,
    canExerciseOptions: activeOptions.hasData
  };
}

function mapTransactionType(apiTypeId: string): TransactionType {
  switch (apiTypeId) {
    case 'GRANT':
      return TransactionType.GRANT;
    case 'VEST':
      return TransactionType.VEST;
    case 'CASHLESS_EXC':
      return TransactionType.CASHLESS_EXERCISE;
    case 'PURCHASE_EXC':
      return TransactionType.PURCHASE_EXERCISE;
    case 'EXPIRE':
      return TransactionType.EXPIRE;
    default:
      throw new Error(`Unknown transaction type: ${apiTypeId}`);
  }
}

/**
 * Simpler mapper for Activity endpoint data
 * Does NOT split options into categories - keeps all options together for activity display
 */
export function mapEmployeeOptionsActivity(
  apiResponse: any
): EmployeeOptionsStatement {
  const employee = mapEmployeeBasicInfo({
    employeeId: apiResponse.employeeId,
    employeeNumber: apiResponse.employeeNumber,
    firstName: apiResponse.firstName,
    lastName: apiResponse.lastName
  });

  // Map all options without categorization
  const options = (apiResponse.options || []).map((apiOption: OptionAgreementApiResponse) =>
    mapOptionAgreement(apiOption)
  );

  // For Activity tab, we just need all options in one place
  const activeOptions: ActiveOptionsData = {
    options: options, // All options go here for activity transformer
    summary: calculateActiveOptionsSummary(options),
    hasData: options.length > 0
  };

  // Empty arrays for other tabs (Activity data shouldn't be used for other tabs)
  const unvestedOptions: ActiveOptionsData = {
    options: [],
    summary: { totalActiveOptions: 0, totalVestedOptions: 0, totalVestedValue: 0, currentPrice: 0 },
    hasData: false
  };

  const cashlessExerciseOptions: CashlessExerciseData = {
    options: [],
    summary: { totalInitialShares: 0, totalCashlessExercisedOptions: 0, totalPurchaseExercisedOptions: 0, totalAllExercisedOptions: 0, totalCashlessProceeds: 0, totalPurchaseCosts: 0, totalNetValue: 0 },
    hasData: false
  };

  const purchaseExerciseOptions: PurchaseExerciseData = {
    options: [],
    summary: { totalInitialShares: 0, totalPurchasedOptions: 0, totalPurchaseCost: 0, averageStrikePrice: 0 },
    hasData: false
  };

  const summary: EmployeeOptionsSummary = {
    totalOptions: options.reduce((sum, opt) => sum + opt.totalShares, 0),
    vestedOptions: options.reduce((sum, opt) => sum + opt.vestedShares, 0),
    exercisedOptions: options.reduce((sum, opt) => sum + opt.exercisedShares, 0),
    vestedWithPositiveValue: 0,
    currentPrice: options.length > 0 ? options[0].currentPrice : 0,
    totalCashlessProceeds: 0,
    totalPurchaseCosts: 0,
    totalNetExerciseValue: 0
  };

  return {
    employee,
    summary,
    activeOptions, // Contains ALL options for activity
    unvestedOptions,
    cashlessExerciseOptions,
    purchaseExerciseOptions,
    hasAnyOptions: options.length > 0,
    canExerciseOptions: false
  };
}